<template>
    <img
        v-if="resolvedUrl"
        :src="resolvedUrl"
        :alt="resolvedName"
        :class="sizeClass"
        class="rounded-full object-cover"
        @error="failed = true"
    />
    <span
        v-else
        :class="sizeClass"
        :style="{ backgroundColor: resolvedColor }"
        class="rounded-full flex items-center justify-center text-white font-semibold select-none"
    >{{ initials }}</span>
</template>

<script setup>
/**
 * The one place that decides what a person looks like.
 *
 * Resolution order, and the reason for it: the photo they uploaded, then the colour stored
 * on their record (which they choose on their own profile page), then a colour seeded from
 * their record id the way the server seeds it, and only for somebody with no record at all
 * — an email sender, an external contact — a hash of their name.
 *
 * Call sites should pass the person object and nothing else. The individual props are kept for the
 * places that genuinely have only a name, and for an author payload that carries no record.
 */
import { computed, ref, watch } from 'vue'
import { personColor, personInitials } from '../utils/personIdentity.js'

const props = defineProps({
    name: { type: String, default: '' },
    /** An explicit override. Leave it unset and the person's own record decides. */
    color: { type: String, default: null },
    avatarUrl: { type: String, default: null },
    size: { type: String, default: 'md' },
    /**
     * The whole person object from an API payload — { id, name, initials, color, avatar_url }.
     * Preferred over the individual props: one binding instead of three, and a call site
     * picks up the uploaded photo automatically rather than needing another prop threaded
     * through.
     */
    person: { type: Object, default: null },
})

// A deleted or moved photo must not leave an empty circle; fall back to initials.
const failed = ref(false)
watch(() => [props.avatarUrl, props.person?.avatar_url], () => { failed.value = false })

const resolvedName = computed(() => props.name || props.person?.name || '')
const resolvedUrl = computed(() => failed.value ? null : (props.avatarUrl ?? props.person?.avatar_url ?? null))

const resolvedColor = computed(() =>
    props.color ?? personColor(props.person ?? resolvedName.value))

const SIZE_CLASSES = {
    xs:  'w-5 h-5 text-[11px]',
    sm:  'w-6 h-6 text-[11px]',
    md:  'w-8 h-8 text-xs',
    lg:  'w-10 h-10 text-sm',
    xl:  'w-12 h-12 text-base',
    '2xl': 'w-20 h-20 text-2xl',
}

const sizeClass = computed(() => SIZE_CLASSES[props.size] ?? SIZE_CLASSES.md)

// The server sends initials with every chip; deriving them again here can only disagree.
const initials = computed(() => props.person?.initials || personInitials(resolvedName.value))
</script>
